<template>
  <div
    class="talent-card"
    :class="{ 'locale-zh-CN': isZhLocale }"
    :style="cardBgStyle"
    @click="$emit('click', item)"
  >
    <div class="avatar-wrap">
      <img :src="avatarSrc" alt="" class="avatar" @error="onAvatarError" />
    </div>
    <div class="card-body">
      <div class="name-row">
        <img v-if="nameDeco" class="name-deco" :src="nameDeco" alt="" @error="onImgError" />
        <span class="name" :title="item.name">{{ item.name || '' }}</span>
        <span class="views">
          <img v-if="iconViews" :src="iconViews" alt="" @error="onImgError" />
          {{ displayViews }}
        </span>
      </div>
      <div class="field-row">
        <img v-if="iconField" class="meta-ico" :src="iconField" alt="" @error="onImgError" />
        <span class="meta-text" :title="displayField">{{ displayField }}</span>
      </div>
      <div class="stats">
        <div class="stat-row">
          <img v-if="iconPublish" class="stat-ico" :src="iconPublish" alt="" @error="onImgError" />
          <span class="stat-label">{{ labels.publishLabel }}</span>
          <span class="stat-badge">{{ displayPublish }}</span>
        </div>
        <div class="stat-row">
          <img v-if="iconTransform" class="stat-ico" :src="iconTransform" alt="" @error="onImgError" />
          <span class="stat-label">{{ labels.transformLabel }}</span>
          <span class="stat-badge">{{ displayTransform }}</span>
        </div>
      </div>
      <div class="region-row">
        <img v-if="iconRegion" class="meta-ico" :src="iconRegion" alt="" @error="onImgError" />
        <span class="meta-text" :title="displayRegion">{{ displayRegion }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import { getTechTalentNewConfig } from '@/config/techTalentNewConfig'
import { getSafeImage, handleImgError } from '@/utils/imageFallback'
import { getCurrentLocale } from '@/utils/i18nText'

const EMPTY_TIP = '待接口提供'

export default {
  name: 'TechTalentCard',
  props: {
    item: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      avatarBroken: false
    }
  },
  computed: {
    isZhLocale() {
      return getCurrentLocale() === 'zh-CN'
    },
    config() {
      return getTechTalentNewConfig(getCurrentLocale())
    },
    labels() {
      return this.config.card || {}
    },
    emptyTip() {
      return this.labels.emptyTip || EMPTY_TIP
    },
    nameDeco() {
      return getSafeImage(this.config.icons?.nameDeco)
    },
    iconViews() {
      return getSafeImage(this.config.icons?.views)
    },
    iconField() {
      return getSafeImage(this.config.icons?.field)
    },
    iconRegion() {
      return getSafeImage(this.config.icons?.region)
    },
    iconPublish() {
      return getSafeImage(this.config.icons?.publish)
    },
    iconTransform() {
      return getSafeImage(this.config.icons?.transform)
    },
    cardBgStyle() {
      const src = getSafeImage(this.labels.cardBg)
      if (!src) return null
      return {
        backgroundImage: `url(${src})`,
        backgroundRepeat: 'no-repeat',
        backgroundSize: '100% 100%',
        backgroundPosition: 'center'
      }
    },
    avatarSrc() {
      if (this.avatarBroken) return getSafeImage(this.labels.avatarFallback)
      return getSafeImage(this.item.avatar || this.labels.avatarFallback)
    },
    displayField() {
      return this.normalizeText(this.item.field)
    },
    displayRegion() {
      return this.normalizeText(this.item.region)
    },
    displayViews() {
      const v = this.item.views
      if (v === undefined || v === null || v === '') return ''
      if (String(v).trim() === this.emptyTip || String(v).trim() === EMPTY_TIP) return ''
      return v
    },
    displayPublish() {
      const v = this.item.publishCount
      if (v === undefined || v === null || v === '') return ''
      if (String(v).trim() === this.emptyTip || String(v).trim() === EMPTY_TIP) return ''
      return v
    },
    displayTransform() {
      const v = this.item.transformCount
      if (v === undefined || v === null || v === '') return ''
      if (String(v).trim() === this.emptyTip || String(v).trim() === EMPTY_TIP) return ''
      return v
    }
  },
  watch: {
    'item.avatar'() {
      this.avatarBroken = false
    }
  },
  methods: {
    normalizeText(val) {
      const text = String(val == null ? '' : val).trim()
      if (!text || text === this.emptyTip || text === EMPTY_TIP) return ''
      return text
    },
    onImgError(e) {
      handleImgError(e)
    },
    onAvatarError(e) {
      this.avatarBroken = true
      handleImgError(e)
    }
  }
}
</script>

<style lang="scss" scoped>
.talent-card {
  display: flex;
  align-items: stretch;
  height: 148px;
  min-height: 148px;
  background-color: #fafafa;
  border: 1px solid #ececec;
  box-sizing: border-box;
  cursor: pointer;
  overflow: hidden;
  transition: box-shadow 0.2s;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06);
  }
}

.avatar-wrap {
  width: 108px;
  flex-shrink: 0;
  align-self: stretch;
  overflow: hidden;
  background: transparent;
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
}

.avatar {
  max-width: 100%;
  max-height: 100%;
  width: auto;
  height: auto;
  object-fit: contain;
  object-position: center;
  display: block;
}

.card-body {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  padding: 10px 12px;
  box-sizing: border-box;
  position: relative;
  z-index: 1;
}

.name-row {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  flex-shrink: 0;
}

.name-deco {
  width: 14px;
  height: 11px;
  object-fit: contain;
  flex-shrink: 0;
}

.name {
  flex: 1;
  min-width: 0;
  font-size: 16px;
  font-weight: 600;
  color: #222;
  line-height: 1.25;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.views {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
  font-size: 12px;
  color: #999;
  line-height: 1;

  img {
    width: 13px;
    height: 11px;
    object-fit: contain;
  }
}

.field-row,
.region-row {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: 6px;
  min-width: 0;
  flex-shrink: 0;
}

.meta-ico {
  width: 12px;
  height: 13px;
  object-fit: contain;
  flex-shrink: 0;
}

.meta-text {
  min-width: 0;
  font-size: 12px;
  color: #888;
  line-height: 1.3;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.stats {
  display: flex;
  flex-direction: row;
  align-items: center;
  flex-wrap: nowrap;
  gap: 12px;
  margin-top: 6px;
  flex-shrink: 0;
  min-width: 0;
}

.stat-row {
  display: flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
  flex: 1;
}

.stat-ico {
  width: 14px;
  height: 14px;
  object-fit: contain;
  flex-shrink: 0;
}

.stat-label {
  font-size: 12px;
  color: #888;
  white-space: nowrap;
}

.stat-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 28px;
  height: 18px;
  padding: 0 8px;
  margin-left: 2px;
  border-radius: 2px;
  background: rgba(160, 144, 60, 0.12);
  font-size: 12px;
  color: #8a7a28;
  line-height: 1;
  box-sizing: border-box;
  white-space: nowrap;
}

.locale-zh-CN {
  .name {
    font-size: 20px;
  }

  .views {
    font-size: 14px;
  }

  .meta-text,
  .stat-label {
    font-size: 15px;
  }

  .stat-badge {
    font-size: 14px;
  }

  .meta-ico,
  .stat-ico {
    width: 16px;
    height: 16px;
  }

  .avatar-wrap {
    width: 120px;
  }

  .name-deco {
    width: 16px;
    height: 16px;
  }
}

</style>
